// Sources lues par Ekera : libellés, ton des étiquettes et gardes de saisie. Aucun secret n'est manipulé ici : la
// seule chose que l'interface déclare est la RÉFÉRENCE d'un secret dans le coffre du client.
import type { CoffreCatalogue, EssaiVue, EtatSource, LogicielCatalogue } from '../api/types';

export const LIBELLE_ETAT_SOURCE: Record<EtatSource, string> = {
  a_jour: 'À jour',
  en_retard: 'En retard',
  en_erreur: 'En erreur',
  jamais_lu: 'Jamais lue',
  desactivee: 'Désactivée',
};

export const TON_ETAT_SOURCE: Record<EtatSource, 'ok' | 'attention' | 'grave' | 'neutre'> = {
  a_jour: 'ok',
  en_retard: 'attention',
  en_erreur: 'grave',
  jamais_lu: 'neutre',
  desactivee: 'neutre',
};

/** Ce que veut dire chaque état, pour le lecteur qui découvre l'écran. */
export const EXPLICATION_ETAT_SOURCE: Record<EtatSource, string> = {
  a_jour: 'La dernière lecture a réussi dans le délai attendu.',
  en_retard: 'La dernière lecture réussie est plus ancienne que le délai toléré : le collecteur est peut-être arrêté.',
  en_erreur: 'La dernière lecture a échoué : voir la cause et le conseil sur la fiche.',
  jamais_lu: 'Déclarée, mais aucune lecture n’a encore été déposée par le collecteur.',
  desactivee: 'Plus lue : l’historique et les objets déjà apportés restent. Elle peut être réactivée.',
};

export const LIBELLE_ETAT_ESSAI: Record<EssaiVue['etat'], string> = {
  demandee: 'Demandé : en attente du collecteur sur site',
  en_cours: 'En cours : le collecteur exécute le test',
  terminee: 'Terminé',
  expiree: 'Expiré : le collecteur n’a pas répondu à temps',
};

export const LIBELLE_ACTION_JOURNAL: Record<string, string> = {
  declaree: 'Déclarée',
  desactivee: 'Désactivée',
  reactivee: 'Réactivée',
};

export const ESSAI_TERMINE = (e: EssaiVue['etat']): boolean => e === 'terminee' || e === 'expiree';

/** Heures → « 24 h », « 2 jours » (affichage). */
export function dureeHeures(h: number): string {
  if (h >= 48 && h % 24 === 0) return `${h / 24} jours`;
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(h)} h`;
}

/** « 1 234 ms » → « 1,2 s ». */
export function dureeMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(ms / 1000)} s`;
}

/** Logiciels du catalogue regroupés par famille (ordre du catalogue). */
export function parFamille(logiciels: readonly LogicielCatalogue[]): { famille: string; logiciels: LogicielCatalogue[] }[] {
  const groupes = new Map<string, LogicielCatalogue[]>();
  for (const l of logiciels) groupes.set(l.famille, [...(groupes.get(l.famille) ?? []), l]);
  return [...groupes].map(([famille, ls]) => ({ famille, logiciels: ls }));
}

/**
 * Garde de saisie de la référence du secret : elle doit commencer par le préfixe d'un coffre admis
 * (« vault:// », « azure-kv:// », « collecteur:// »…). Un mot de passe collé par erreur n'a pas cette forme : il n'est
 * donc jamais envoyé. Le message ne recopie pas la valeur. L'API valide ensuite la forme complète.
 */
export function problemeReference(valeur: string, coffres: readonly CoffreCatalogue[]): string | null {
  const v = valeur.trim();
  if (!v) return 'Indiquez la référence du secret dans votre coffre.';
  const prefixes = coffres.map((c) => c.schema);
  if (!prefixes.some((p) => v.startsWith(p)))
    return `Ce n’est pas une référence de coffre : elle doit commencer par ${prefixes.map((p) => `« ${p} »`).join(', ')}. Ne saisissez jamais le mot de passe ici.`;
  if (/\s/.test(v)) return 'Une référence de coffre ne contient pas d’espace.';
  return null;
}

/** Les champs de la déclaration qui seront envoyés (sans valeur vide). */
export interface ChampsDeclaration {
  nom: string;
  adresse: string;
  compte: string;
  reference: string;
  frequence: string;
}

export function frequenceValide(s: string): boolean {
  if (!s.trim()) return true;
  const n = Number(s);
  return Number.isInteger(n) && n >= 1 && n <= 720;
}

/** Type d'une pièce lue par Ekera (manifeste du diagnostic), en clair. */
export const LIBELLE_TYPE_PIECE: Record<string, string> = {
  'doc-pdf': 'Document PDF',
  'doc-docx': 'Document Word',
  'doc-xlsx': 'Classeur Excel',
  'glpi-json': 'Export GLPI (inventaire)',
  'ad-csv': 'Export Active Directory',
  firewall: 'Configuration de pare-feu',
  'm365-csv': 'Export Microsoft 365',
  'cloud-json': 'Export d’un service en ligne',
  'tickets-csv': 'Tickets d’assistance',
  'achats-csv': 'Achats',
  'fec-txt': 'Écritures comptables (FEC)',
  note: 'Note',
};
